import { getAuth, useAuthStore } from '@/store/authStore';
import { useUiStore } from '@/store/uiStore';

/**
 * The one place every HTTP request in the app goes through.
 *
 * Phase 1 shipped a deliberately thin version of this in lib/apiClient.js so
 * the swap would be a single-file change rather than a sweep through forty
 * call sites. This is that swap. What it adds over the thin version:
 *
 *   - tenant context injection, so no call site builds a tenant path by hand
 *   - retry with exponential backoff + jitter, for transient failures only
 *   - Accept-Language, from the same store that drives the UI language
 *   - a request timeout, because a hung fetch otherwise hangs forever
 *
 * What it deliberately does NOT do: caching, deduplication or refetch
 * policy. React Query owns all of that, and a second layer doing it here
 * would be a second source of truth about staleness.
 */
export const API_BASE = import.meta.env.VITE_API_BASE ?? '';

/** A slow mobile connection in a quarry is normal; an infinite wait is not. */
const DEFAULT_TIMEOUT_MS = 20_000;
// Generated documents (the dwell-report PDF/XLSX) are rendered per request
// rather than served from cache, so they are allowed longer than a lookup.
const DOCUMENT_TIMEOUT_MS = 60_000;
const MAX_ATTEMPTS = 3;
const BASE_BACKOFF_MS = 400;

export class ApiError extends Error {
  constructor(message, status, body) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }

  /** 4xx means the request itself was wrong — sending it again changes
   *  nothing. Everything else might succeed on a second try. */
  get isRetryable() {
    return this.status === undefined || this.status === 408 || this.status === 429 || this.status >= 500;
  }
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Full jitter (AWS's formulation): a random point in [0, cap] rather than a
 * fixed delay. Without it, every tab that failed on the same backend restart
 * retries at the same instant and re-creates the spike that caused the
 * failure.
 */
function backoffDelay(attempt) {
  const cap = BASE_BACKOFF_MS * 2 ** (attempt - 1);
  return Math.random() * cap;
}

/**
 * Retrying a mutation can double-charge, double-dispatch or double-cancel.
 * Only methods the HTTP spec defines as idempotent are retried; a POST is
 * surfaced to the caller on first failure, which is the safe default even
 * though it means the occasional visible error.
 */
const IDEMPOTENT = new Set(['GET', 'HEAD', 'OPTIONS', 'PUT', 'DELETE']);

/**
 * Read by the backend since Phase 9.1 (core/i18n.py), which is what this
 * comment used to say was still ahead. Two things depend on it now:
 * documents rendered during a request (the dwell-report export, when no
 * explicit ?lang= is given), and — for a driver's own calls — the stored
 * `drivers.language` that decides what language their Telegram messages and
 * proof-of-delivery PDF come out in. That last one is why this header still
 * matters on requests whose response contains no text at all: it is how the
 * Driver App tells the server which language to reach the driver in later,
 * from background threads that have no request to read.
 */
function acceptLanguage() {
  const lang = useUiStore.getState().lang || 'lt';
  return lang === 'lt' ? 'lt-LT,lt;q=0.9,en;q=0.6' : 'en-GB,en;q=0.9,lt;q=0.6';
}

/**
 * Builds a query string, dropping empty values so a filter that is merely
 * unset never reaches the API as `&status=`.
 */
export function qs(params) {
  const search = new URLSearchParams();
  Object.entries(params ?? {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') search.set(key, value);
  });
  const string = search.toString();
  return string ? `?${string}` : '';
}

async function parseError(response) {
  const text = await response.text().catch(() => '');
  let parsed = null;
  try {
    parsed = JSON.parse(text);
  } catch {
    // Several backend errors come back as plain text rather than JSON.
  }
  return new ApiError(parsed?.error || text || `HTTP ${response.status}`, response.status, parsed);
}

/**
 * @param {string} path        absolute API path, e.g. `/api/orders`
 * @param {object} [options]   fetch options, plus:
 *   body      any JSON-serialisable value, or a FormData
 *   timeout   ms before the request is aborted (default 20 000)
 *   retry     false to opt out entirely
 *   signal    caller's AbortSignal — respected alongside the timeout
 */
export async function api(path, options = {}) {
  const { body, headers, timeout = DEFAULT_TIMEOUT_MS, retry = true, signal, ...rest } = options;
  const method = (rest.method ?? 'GET').toUpperCase();
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData;
  const canRetry = retry && IDEMPOTENT.has(method);

  let lastError;
  for (let attempt = 1; attempt <= (canRetry ? MAX_ATTEMPTS : 1); attempt += 1) {
    // A fresh controller per attempt: an aborted signal stays aborted, so
    // reusing one would make every retry fail instantly.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    const onExternalAbort = () => controller.abort();
    signal?.addEventListener('abort', onExternalAbort);

    try {
      const { apiKey } = getAuth();
      const response = await fetch(API_BASE + path, {
        ...rest,
        signal: controller.signal,
        headers: {
          Accept: 'application/json',
          'Accept-Language': acceptLanguage(),
          ...(body !== undefined && !isFormData ? { 'Content-Type': 'application/json' } : null),
          ...(apiKey ? { Authorization: `Bearer ${apiKey}` } : null),
          ...headers,
        },
        body: body !== undefined && !isFormData && typeof body !== 'string' ? JSON.stringify(body) : body,
      });

      // A revoked or rotated key is permanent: clear the session so the login
      // gate appears, instead of leaving every panel failing with no way back.
      if (response.status === 401) {
        // The key is already refused; see signOut()'s notifyServer.
        useAuthStore.getState().signOut({ notifyServer: false });
        throw new ApiError('unauthorized', 401);
      }

      if (!response.ok) throw await parseError(response);
      if (response.status === 204) return null;
      return await response.json();
    } catch (error) {
      // The caller's own abort is an intent, not a failure to retry through.
      if (signal?.aborted) throw error;

      lastError =
        error instanceof ApiError
          ? error
          : new ApiError(
              error.name === 'AbortError' ? 'request timed out' : error.message,
              undefined,
              null,
            );

      const isLastAttempt = attempt >= (canRetry ? MAX_ATTEMPTS : 1);
      if (isLastAttempt || !lastError.isRetryable) throw lastError;
      await sleep(backoffDelay(attempt));
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener('abort', onExternalAbort);
    }
  }

  throw lastError;
}

/** Multipart upload. Content-Type is left unset on purpose so the browser
 *  can generate its own boundary — setting it by hand is the classic way to
 *  make a working upload start failing. Never retried: it is a POST. */
export function apiUpload(path, formData) {
  return api(path, { method: 'POST', body: formData, retry: false, timeout: 60_000 });
}

/**
 * Fetches a generated document and hands it to the browser as a download.
 *
 * A plain `<a href="/api/.../export.pdf" download>` cannot do this: every
 * export route is admin-authenticated and the key lives in a header, which
 * an anchor navigation has no way to send. So the file is fetched like any
 * other call and turned into an object URL.
 *
 * The filename is supplied by the caller rather than read off
 * Content-Disposition, because Flask's send_from_directory() defaults to
 * `as_attachment=False` and sends no filename at all — the browser would
 * otherwise save the last path segment, which for these routes is literally
 * "export.pdf" for every tenant and every month.
 *
 * Longer timeout than a normal call: these responses are rendered on demand
 * (reportlab for the PDF, openpyxl for the sheet), never cached, and a
 * month with a lot of stops is real work rather than a lookup.
 */
export async function apiDownload(path, filename) {
  const { apiKey } = getAuth();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), DOCUMENT_TIMEOUT_MS);
  try {
    const response = await fetch(API_BASE + path, {
      signal: controller.signal,
      headers: {
        'Accept-Language': acceptLanguage(),
        ...(apiKey ? { Authorization: `Bearer ${apiKey}` } : null),
      },
    });
    if (response.status === 401) {
      // The key is already refused; see signOut()'s notifyServer.
      useAuthStore.getState().signOut({ notifyServer: false });
      throw new ApiError('unauthorized', 401);
    }
    // A failed export answers with this API's normal JSON error body, so it
    // is parsed the same way every other call parses one.
    if (!response.ok) throw await parseError(response);

    const blob = await response.blob();
    saveBlob(blob, filename);
  } catch (error) {
    if (error instanceof ApiError) throw error;
    throw new ApiError(error.name === 'AbortError' ? 'request timed out' : error.message, undefined, null);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * The DOM half of apiDownload, kept separate so the fetch above stays
 * testable without a real browser download. The object URL is revoked on
 * the next tick rather than immediately: revoking it synchronously after
 * click() races the browser's own read of the URL in several engines, and
 * the symptom is a download that silently produces a zero-byte file.
 */
function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** POST /api/auth/login — the one unauthenticated call in the console. */
export async function login(username, password) {
  return api('/api/auth/login', {
    method: 'POST',
    body: { username, password },
    retry: false,
  });
}

/** POST /api/auth/telegram — Mini App sign-in (see blueprints/telegram.py). */
export async function loginWithTelegram(initData) {
  return api('/api/auth/telegram', {
    method: 'POST',
    body: { init_data: initData },
    retry: false,
  });
}

/** The largest page core/paging.py will serve. A bigger `limit` is clamped
 *  down to this silently, which is exactly why fetchAllPages exists. */
export const MAX_SERVER_PAGE = 100;

/**
 * Every row of a paginated list endpoint, by following `total_count`.
 *
 * The three entity lists (clients, quarries, vehicles) answer with
 * `{data, total_count}` and cap a page at 100 — but several screens need
 * the COMPLETE set rather than a page of it: the dispatcher map draws every
 * quarry, and the order form, the approval modal and the defect form all
 * offer one in a picker. A cap there does not fail, it silently omits, and
 * a picker missing the truck you need looks like the truck was deleted.
 *
 * Bounded by construction: it stops as soon as it holds `total_count` rows,
 * so it is one request for anything that fits in a page. Use it only where
 * the whole set is genuinely needed — a caller that just wants a count
 * should read `total_count` off a single page instead.
 */
export async function fetchAllPages(path, params) {
  // One qs() per URL. Concatenating two would emit a second '?', and the
  // filters after it would be read as part of a value — silently widening
  // the result instead of narrowing it.
  const page = (offset) => api(`${path}${qs({ ...params, limit: MAX_SERVER_PAGE, offset })}`);

  const first = await page(0);
  const total = first.total_count ?? first.data.length;
  const rows = [...first.data];
  while (rows.length < total) {
    const next = await page(rows.length);
    if (next.data.length === 0) break; // Defensive: never loop on an empty page.
    rows.push(...next.data);
  }
  return { data: rows, total_count: total };
}
